import React, { useState } from 'react';
import PanchTatvaButton from './PanchTatvaButton';
import { adminRetryShipment } from '../services/shipmentService';

/**
 * RetryShipmentModal
 *
 * Admin-only modal for force-retrying a permanently failed shipment.
 *
 * SAFETY DESIGN:
 * - Only shown when order.shiprocket.status === 'permanent_failed'
 * - Button is disabled while request is in-flight (prevents double-submit)
 * - 409 response = "already processed" (not an error — display gracefully)
 * - 429 response = rate limited (show wait message, do NOT auto-retry)
 * - 503 response = feature disabled (show retry message)
 * - Does NOT assume shipment exists after retry (async system — caller must re-poll)
 *
 * @param {Object}   order          - Full order object (must have orderId)
 * @param {Function} getIdToken     - Async fn returning Firebase ID token
 * @param {Function} onClose        - Called when modal is dismissed
 * @param {Function} onRetryQueued  - Called on successful queue — caller should re-enable polling
 * @param {Object}   strings        - strings.adminShipment from i18n
 */
export default function RetryShipmentModal({ order, getIdToken, onClose, onRetryQueued, strings }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success' | 'error' | 'already' | 'rateLimit', message }

  const s = strings || {};

  const handleRetry = async () => {
    if (loading) return; // Safety guard — prevent double-submit
    setLoading(true);
    setResult(null);

    try {
      const idToken = await getIdToken();
      const res = await adminRetryShipment(order.orderId, reason.trim(), idToken);

      if (res === null) {
        // 503 — feature disabled
        setResult({ type: 'error', message: s.retryServiceUnavailable || 'Shipment service is currently unavailable. Please try again later.' });
        return;
      }

      if (res.alreadyCreated) {
        // 409 — shipment already created (not a real failure — show info)
        setResult({ type: 'already', message: s.retryAlreadyCreated || 'This shipment was already created successfully. Refresh the order to see the latest status.' });
        return;
      }

      // Success — queued
      setResult({ type: 'success', message: s.retryQueued || 'Retry queued successfully. The shipment will be created in the background — refresh in a few seconds.' });
      if (onRetryQueued) onRetryQueued(order);
    } catch (err) {
      if (err.status === 429) {
        // Rate limited — do NOT auto-retry
        setResult({ type: 'rateLimit', message: s.retryRateLimit || 'You are being rate limited. Please wait a moment before trying again.' });
      } else if (err.status === 412) {
        // Shipment not yet in a retryable state — ignore silently
        setResult({ type: 'error', message: s.retryNotReady || 'Order is not ready for retry yet. Please wait and try again.' });
      } else {
        setResult({ type: 'error', message: err.message || (s.retryError || 'Failed to queue retry. Please try again.') });
      }
    } finally {
      setLoading(false);
    }
  };

  const resultStyles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    already: 'bg-blue-50 border-blue-200 text-blue-800',
    rateLimit: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-cardBg rounded-2xl shadow-xl p-8 max-w-md w-full border border-secondary/10">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-heading text-headingText mb-1">
            {s.retryModalTitle || 'Retry Shipment'}
          </h2>
          <p className="text-sm text-textSecondary font-sans">
            {s.retryModalSubtitle || 'This will queue a new shipment creation attempt. The result is async — check back in a few seconds.'}
          </p>
        </div>

        {/* Order info */}
        <div className="bg-surface rounded-xl p-4 mb-6 border border-secondary/10">
          <p className="text-xs text-textSecondary font-sans mb-1">{s.retryOrderId || 'Order ID'}</p>
          <p className="text-sm font-medium text-headingText font-sans">{order.orderId}</p>
        </div>

        {/* Result message (success / error / rate limit) */}
        {result && (
          <div className={`border rounded-xl px-4 py-3 mb-6 text-sm font-sans ${resultStyles[result.type] || resultStyles.error}`}>
            {result.message}
          </div>
        )}

        {/* Reason field — only show if no terminal result yet */}
        {!result && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-headingText font-sans mb-2">
              {s.retryReason || 'Reason for retry'}
              <span className="text-textSecondary font-normal ml-1">({s.optional || 'optional'})</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={s.retryReasonPlaceholder || 'e.g. Address confirmed correct, courier issue resolved…'}
              rows={3}
              className="w-full px-4 py-3 border border-secondary/20 rounded-xl bg-surface text-headingText font-sans text-sm placeholder-textSecondary focus:outline-none focus:border-secondary resize-none"
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3">
          {/* Show retry button only if no terminal result, or if it was a retryable error */}
          {(!result || result.type === 'error' || result.type === 'rateLimit') && (
            <PanchTatvaButton
              variant="primary"
              size="default"
              fullWidth
              onClick={handleRetry}
              disabled={loading}
            >
              {loading
                ? (s.retryInProgress || 'Queuing…')
                : (s.retryConfirm || 'Queue Retry')}
            </PanchTatvaButton>
          )}
          <PanchTatvaButton
            variant="outline"
            size="default"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            {result?.type === 'success' || result?.type === 'already'
              ? (s.close || 'Close')
              : (s.cancel || 'Cancel')}
          </PanchTatvaButton>
        </div>

        {/* Async disclaimer */}
        <p className="text-xs text-textSecondary font-sans text-center mt-4 leading-relaxed">
          {s.retryAsyncNote || '⚡ Retry is async — shipment creation happens in the background. The order will update within 30 seconds.'}
        </p>
      </div>
    </div>
  );
}
